import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { REGISTER, AUTH, INVITE } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import api, { formatApiErrorDetail, setAuthToken } from '@/lib/api';
import MathCaptcha from '@/components/MathCaptcha';
import { readStoredReferral, storeReferral, clearStoredReferral } from '@/lib/referral';
import { Sparkles, Loader2, Mic2, Briefcase, Camera, User, Eye, EyeOff, Gift } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const ACCOUNT_TYPES = [
  { id: 'artist', label: 'Artist', desc: 'I perform / create', icon: Mic2, testId: AUTH.accountTypeArtist, color: 'from-fuchsia-500 to-purple-700' },
  { id: 'organizer', label: 'Event Organizer', desc: 'I host events', icon: Briefcase, testId: AUTH.accountTypeOrganizer, color: 'from-cyan-500 to-blue-700' },
  { id: 'casting_director', label: 'Casting Director', desc: 'I hire talent', icon: Camera, testId: AUTH.accountTypeCasting, color: 'from-amber-400 to-orange-500' },
  { id: 'fan', label: 'Fan / Discover', desc: 'I just want to explore', icon: User, testId: AUTH.accountTypeFan, color: 'from-emerald-400 to-teal-600' },
];

const Register = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accountType, setAccountType] = useState('artist');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [captcha, setCaptcha] = useState({ captcha_id: '', answer: '' });
  const captchaRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referrer, setReferrer] = useState(null);

  // Pick referral from ?ref= or localStorage; fetch referrer info for friendly banner
  useEffect(() => {
    const fromQuery = searchParams.get('ref');
    const code = fromQuery ? storeReferral(fromQuery) : readStoredReferral();
    if (!code) return;
    setReferralCode(code);
    (async () => {
      try {
        const { data } = await api.get(`/referrals/lookup/${code}`);
        setReferrer(data);
      } catch {
        setReferrer(null);
      }
    })();
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/register', {
        email, password, name, account_type: accountType,
        captcha_id: captcha.captcha_id, captcha_answer: captcha.answer,
        referral_code: referralCode || undefined,
      });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      clearStoredReferral();
      // Route based on account type
      if (accountType === 'artist') navigate('/onboarding');
      else if (accountType === 'organizer') navigate('/post/event');
      else if (accountType === 'casting_director') navigate('/post/casting');
      else navigate('/dashboard');
    } catch (err) {
      console.error('[Maarivil] register failed:', err);
      setError(formatApiErrorDetail(err.response?.data?.detail, err));
      captchaRef.current?.refresh();
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl" />
      </div>
      <div className="w-full max-w-2xl glass-strong rounded-3xl border border-white/10 p-10">
        {referrer && (
          <div
            data-testid={INVITE.registerBanner}
            className="mb-6 flex items-center gap-3 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/5 px-4 py-3 text-sm"
          >
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 flex items-center justify-center shrink-0">
              <Gift className="h-4 w-4 text-white" />
            </div>
            <div className="text-slate-200">
              <span className="text-fuchsia-200 font-medium">{referrer.name}</span> invited you to Maarivil.
              <span className="text-slate-400"> Your signup will be credited to them.</span>
            </div>
          </div>
        )}
        <div className="flex justify-center mb-6">
          <img src="/maarivil-logo.png" alt="Maarivil" className="h-16 w-auto object-contain drop-shadow-[0_0_20px_rgba(217,70,239,0.3)]" />
        </div>
        <h1 className="font-display text-3xl font-bold text-center text-white mb-2">Join Maarivil</h1>
        <p className="text-slate-400 text-center mb-8 text-sm">Pick your role to get a tailored experience.</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {ACCOUNT_TYPES.map((t) => {
            const Icon = t.icon;
            const active = accountType === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={t.testId}
                onClick={() => setAccountType(t.id)}
                className={`relative rounded-2xl p-4 border transition-all text-left overflow-hidden ${active ? 'border-fuchsia-400/60 bg-white/5' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
              >
                {active && <div className={`absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br ${t.color} opacity-40 blur-2xl`} />}
                <div className={`relative h-9 w-9 rounded-lg bg-gradient-to-br ${t.color} flex items-center justify-center mb-3`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div className="text-white text-sm font-medium">{t.label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{t.desc}</div>
              </button>
            );
          })}
        </div>

        <button
          onClick={googleLogin}
          data-testid={AUTH.googleButton}
          className="w-full glass border border-white/10 hover:border-fuchsia-400/40 transition rounded-xl py-3 text-white flex items-center justify-center gap-2 mb-6"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-slate-500 uppercase tracking-widest">or email</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid={REGISTER.nameInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid={REGISTER.emailInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Password</Label>
              <div className="relative mt-1">
                <Input
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  type={showPassword ? 'text' : 'password'} required data-testid={REGISTER.passwordInput}
                  className="bg-white/5 border-white/10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="register-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Confirm</Label>
              <div className="relative mt-1">
                <Input
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  type={showConfirm ? 'text' : 'password'} required data-testid={REGISTER.passwordConfirmInput}
                  className="bg-white/5 border-white/10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  data-testid="register-confirm-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
          <MathCaptcha ref={captchaRef} onChange={setCaptcha} />
          {error && <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">{error}</div>}
          <Button type="submit" disabled={loading} data-testid={REGISTER.submitButton} className="w-full btn-rainbow rounded-xl py-6 text-base text-white border-0">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…</> : 'Create account'}
          </Button>
        </form>

        <p className="text-center text-sm text-slate-400 mt-6">
          Already have an account?{' '}
          <Link to="/login" data-testid={REGISTER.loginLink} className="text-fuchsia-300 hover:text-fuchsia-200">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
